import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { CContainer, CRow, CCol, CCard, CCardBody, CSpinner, CButton, CBadge, CCollapse } from '@coreui/react';
import api from '../../api/axiosInstance';
import MathRenderer from '../../components/MathRenderer';
import { FaFacebookF, FaWhatsapp, FaXTwitter } from 'react-icons/fa6';
import 'katex/dist/katex.min.css';
import swal from 'sweetalert2';
import AnnouncementBanner from '../../components/AnnouncementBanner';

const ViewQuestion = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [questionData, setQuestionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSolution, setShowSolution] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Access check state
  const [hasSubscription, setHasSubscription] = useState(false);

  const token = localStorage.getItem('authToken');
  const userId = localStorage.getItem('ID') || localStorage.getItem('memberId');

  useEffect(() => {
    const fetchQuestionDetailsAndCheckAccess = async () => {
      try {
        setLoading(true);
        setError(null);

        // 1. Fetch Question Data
        const res = await api.get(`/api/Content/GetQuestionById/${id}`);
        const data = res.data?.data || res.data;
        setQuestionData(data);

        // 2. Section aur Access Verification Logic
        const levelNameStr = String(data?.levelName || data?.LevelName || '');
        const sectionStr = String(data?.section || data?.Section || data?.sectionName || data?.SectionName || '');
        
        const isAlphaSection = levelNameStr.toLowerCase().includes('alpha') || 
                               sectionStr.toLowerCase().includes('alpha');

        if (!token) {
          // Login nahi hai toh turant login page par bhej dein
          navigate('/login', { state: { from: location.pathname } });
          return;
        }

        if (isAlphaSection) {
          setHasSubscription(true);
        } else {
          // 1. Check LocalStorage activeTiers for Sigma Plan
          const activeTiers = JSON.parse(localStorage.getItem('activeTiers') || '[]');
          const hasSigmaTier = activeTiers.some(
            (tier) => String(tier).trim().toLowerCase() === 'sigma'
          );

          if (hasSigmaTier) {
            setHasSubscription(true);
            setLoading(false);
            return;
          }

          try {
            const subRes = await api.get(`/api/members/${userId}/subscription`);
            const subData = subRes.data?.data || subRes.data;
            const userSubs = Array.isArray(subData) ? subData : subData ? [subData] : [];

            // API Response me check karein ki kisi subscription me Sigma tier active hai ya nahi
            const isSigmaInApi = userSubs.some((sub) => {
              const tierName = String(sub?.tierName || sub?.tier || sub?.sectionName || '').toLowerCase();
              return tierName.includes('sigma');
            });

            if (isSigmaInApi) {
              setHasSubscription(true);
              setLoading(false);
              return;
            }

            // Category match check
            const allowedCategoryIds = userSubs.flatMap((sub) => {
              const ids = sub.categoryIds || sub.CategoryIds || '';
              return String(ids).split(',').map((catId) => catId.trim());
            });

            const qCatId = data.categoryId || data.CategoryId || data.category_id;
            const questionCatIds = Array.isArray(qCatId) 
              ? qCatId.map((catId) => String(catId).trim()) 
              : qCatId !== undefined && qCatId !== null 
                ? String(qCatId).split(',').map((catId) => catId.trim()) 
                : [];

            const isSubscribed = questionCatIds.some((catId) => allowedCategoryIds.includes(catId));

            if (!isSubscribed) {
              // Popup dikhayen aur confirm par profile page par bhej dein
              swal.fire({
                title: "Subscription Required",
                text: "To view this content, please subscribe to this category.",
                icon: "warning",
                confirmButtonText: "Subscribe Now",
                allowOutsideClick: false
              }).then(() => {
                navigate('/profile', { state: { from: location.pathname } });
              });
              return;
            }

            setHasSubscription(true);
          } catch (subErr) {
            console.error("Subscription verification failed:", subErr);
            navigate('/profile');
            return;
          }
        }
      } catch (err) {
        console.error("Error fetching question details:", err);
        setError("Failed to load question details or question not found.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchQuestionDetailsAndCheckAccess();
  }, [id, navigate, location.pathname, token, userId]);

  const handleToggleSolution = () => {
    if (!token) {
      swal.fire({
        title: "Login Required",
        text: "Please login to view the solution.",
        icon: "info",
        confirmButtonText: "Login",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/login', { state: { from: location.pathname } });
        }
      });
      return;
    }
    setShowSolution(!showSolution);
  };

  const handleShare = (platform) => {
    const shareUrl = window.location.href;
    if (platform === 'whatsapp') {
      window.open(`https://api.whatsapp.com/send?text=Check out this question: ${encodeURIComponent(shareUrl)}`, '_blank');
    } else if (platform === 'facebook') {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
    } else if (platform === 'twitter') {
      window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}`, '_blank');
    }
  };

  // PDF Download Handler
  const handleDirectDownloadPDF = async () => {
    if (!token) {
      swal.fire({
        title: "Login Required",
        text: "Please login first to download the PDF.",
        icon: "info",
        confirmButtonText: "Login",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/login', { state: { from: location.pathname } });
        }
      });
      return;
    }

    try {
      setDownloadingPdf(true);
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'fixed';
      printFrame.style.right = '0';
      printFrame.style.bottom = '0';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = '0';
      document.body.appendChild(printFrame);

      const frameDoc = printFrame.contentWindow.document;

      const rawQuestionHtml = questionData.questionText || questionData.QuestionText || questionData.questionStatement || '';
      const correctedQuestionHtml = rawQuestionHtml.replace(
        /src="\/uploads\//g,
        'src="https://apiambipie.mohdwebsolution.in/uploads/'
      );

      const rawSolutionHtml = questionData.solution || questionData.SolutionText || questionData.Solution || '';
      const correctedSolutionHtml = rawSolutionHtml.replace(
        /src="\/uploads\//g,
        'src="https://apiambipie.mohdwebsolution.in/uploads/'
      );

      const isSubjective = (questionData.questionType || questionData.QuestionType) === 'Subjective';

      let optionsHtml = '';
      if (!isSubjective) {
        const activeOpts = [
          { label: 'A', value: questionData.optionA || questionData.OptionA },
          { label: 'B', value: questionData.optionB || questionData.OptionB },
          { label: 'C', value: questionData.optionC || questionData.OptionC },
          { label: 'D', value: questionData.optionD || questionData.OptionD }
        ];

        const optEVal = questionData.optionE || questionData.OptionE;
        if (optEVal && optEVal.toString().trim() !== '') {
          activeOpts.push({ label: 'E', value: optEVal });
        }

        optionsHtml = `
          <div class="section-title" style="margin-top: 15px;">OPTIONS MATRIX:</div>
          <div style="display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 20px;">
            ${activeOpts.map(opt => `
              <div style="width: 48%; box-sizing: border-box; border: 1px solid #ddd; padding: 8px; background: #fff; border-radius: 4px;">
                <strong>(${opt.label})</strong>${opt.value || ''}
              </div>
            `).join('')}
          </div>
        `;
      }

      const customId = questionData?.customQuestionId || `LAAC${id}`;

      frameDoc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${customId}</title>
            <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.css">
            <script defer src="https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.js"></script>
            <script defer src="https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/contrib/auto-render.min.js"></script>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
              .header { border-bottom: 2px solid #002147; padding-bottom: 10px; margin-bottom: 20px; display: flex; justify-content: space-between; }
              .title { color: #002147; margin: 0; font-weight: bold; font-size: 20px; }
              .sub { color: #666; font-size: 12px; margin: 0; }
              table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 13px; background: #f9f9f9; }
              td { padding: 8px; border: 1px solid #ddd; width: 50%; }
              .section-title { color: #002147; font-weight: bold; margin-bottom: 10px; font-size: 14px; }
              .content-box { font-size: 14px; line-height: 1.6; padding-left: 10px; border-left: 3px solid #002147; margin-bottom: 20px; }
              img { max-width: 100% !important; height: auto !important; display: block !important; margin: 15px auto !important; }
              .sol-box { font-size: 14px; line-height: 1.6; background: #f4faf6; padding: 12px; border-left: 3px solid #198754; }
              .katex-display { text-align: left !important; margin: 8px 0 !important; }
              .katex-display > .katex { text-align: left !important; display: inline-block !important; }
              @page { size: A4; margin: 15mm; }
            </style>
          </head>
          <body>
            <div class="header">
              <div>
                <h2 class="title">AMBIPIE LEARNING PORTAL</h2>
                <p class="sub">Verified Academic Core Syllabus Resource Repository</p>
              </div>
              <div style="text-align: right;">
                <strong style="font-size: 14px; color: #002147;">ID: ${customId}</strong><br/>
                <span style="font-size: 12px; color: #555;">Type: ${questionData?.questionType || questionData?.QuestionType || 'Objective'}</span>
              </div>
            </div>

            <table>
              <tr>
                <td><strong>Chapter:</strong> ${questionData?.chapterName || questionData?.ChapterName || 'N/A'}</td>
                <td><strong>Topic:</strong> ${questionData?.topicName || questionData?.TopicName || 'N/A'}</td>
              </tr>
              <tr>
                <td><strong>Exam Reference:</strong> ${questionData?.askedIn || questionData?.AskedIn || 'N/A'} - ${questionData?.askedYear || questionData?.year || 'N/A'}</td>
                <td><strong>Book Source:</strong> ${questionData?.bookReference || questionData?.BookReference || 'N/A'}</td>
              </tr>
            </table>

            <div class="section-title">QUESTION STATEMENT:</div>
            <div class="content-box">${correctedQuestionHtml}</div>

            ${optionsHtml}

            ${
              correctedSolutionHtml
                ? `<div class="section-title" style="color: #198754; margin-top: 25px;">DETAILED SOLUTION:</div>
                   <div class="sol-box">${correctedSolutionHtml}</div>`
                : ''
            }

            <script>
              window.addEventListener('DOMContentLoaded', () => {
                if (window.renderMathInElement) {
                  renderMathInElement(document.body, {
                    delimiters: [
                      { left: '$$', right: '$$', display: true },
                      { left: '$', right: '$', display: false },
                      { left: '\\(', right: '\\)', display: false },
                      { left: '\\[', right: '\\]', display: true }
                    ],
                    throwOnError: false
                  });
                }
              });
            </script>
          </body>
        </html>
      `);

      frameDoc.close();

      setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
        document.body.removeChild(printFrame);
        setDownloadingPdf(false);
      }, 1200);

    } catch (err) {
      console.error("PDF generation error:", err);
      alert("Failed to process PDF.");
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
        <CSpinner color="primary" size="lg" />
      </div>
    );
  }

  if (error || !questionData) {
    return (
      <CContainer className="text-center mt-5 py-5">
        <h4 className="text-danger mb-3">{error || "Data load error"}</h4>
        <CButton color="primary" onClick={() => navigate(-1)}>Go Back</CButton>
      </CContainer>
    );
  }

  const isSubjectiveMode = (questionData.questionType || questionData.QuestionType) === 'Subjective';

  const activeOptions = [
    { key: 'optionA', label: 'A', value: questionData.optionA || questionData.OptionA },
    { key: 'optionB', label: 'B', value: questionData.optionB || questionData.OptionB },
    { key: 'optionC', label: 'C', value: questionData.optionC || questionData.OptionC },
    { key: 'optionD', label: 'D', value: questionData.optionD || questionData.OptionD }
  ];

  const optE = questionData.optionE || questionData.OptionE;
  if (optE && optE.toString().trim() !== '') {
    activeOptions.push({ key: 'optionE', label: 'E', value: optE });
  }

  const questionStatementContent = questionData.questionText || questionData.QuestionText || questionData.questionStatement || questionData.QuestionStatement;
  const solutionContent = questionData.solution || questionData.SolutionText || questionData.Solution;

  return (
    <CContainer className="py-4">
      {/* Top Header Bar */}
      <div className="d-flex justify-content-between align-items-center p-3 text-white rounded-top shadow-sm" style={{ backgroundColor: 'rgb(0, 33, 71)' }}>
        <div className="d-flex align-items-center gap-3">
          <h4 className="m-0 fw-bold" style={{ color: 'yellow' }}>Question Details</h4>
          <CBadge className="p-2" style={{ fontSize: '0.85rem', color:'white', backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: '6px' }}>
            ID: {questionData.customQuestionId || `LAAC000${id}`}
          </CBadge>
        </div>
      </div>

      {/* Syllabus Metadata Track */}
      <div className="bg-white border-bottom p-3 shadow-sm mb-4">
        <div className="text-muted small fw-bold mb-3 d-flex align-items-center gap-2">
          📌 ACADEMIC SYLLABUS METADATA TRACK
        </div>
        <CRow className="g-3 text-center">
          <CCol xs={6} sm={4} md={3} lg={2}>
            <div className="border p-2 rounded bg-light shadow-xs h-100">
              <div className="text-muted small text-uppercase fw-bold mb-1" style={{ fontSize: '11px' }}>Chapter</div>
              <div className="fw-bold text-primary text-truncate px-1" style={{ fontSize: '13px' }}>{questionData.chapterName || questionData.ChapterName || 'N/A'}</div>
            </div>
          </CCol>
          <CCol xs={6} sm={4} md={3} lg={2}>
            <div className="border p-2 rounded bg-light shadow-xs h-100">
              <div className="text-muted small text-uppercase fw-bold mb-1" style={{ fontSize: '11px' }}>Topic</div>
              <div className="fw-bold text-dark text-truncate px-1" style={{ fontSize: '13px' }}>{questionData.topicName || questionData.TopicName || 'N/A'}</div>
            </div>
          </CCol>
          <CCol xs={6} sm={4} md={3} lg={2}>
            <div className="border p-2 rounded bg-light shadow-xs h-100">
              <div className="text-muted small text-uppercase fw-bold mb-1" style={{ fontSize: '11px' }}>Asked In</div>
              <div className="fw-bold text-info text-truncate px-1" style={{ fontSize: '13px' }}>{questionData.askedIn || questionData.AskedIn || 'N/A'}</div>
            </div>
          </CCol>
          <CCol xs={6} sm={4} md={3} lg={2}>
            <div className="border p-2 rounded bg-light shadow-xs h-100">
              <div className="text-muted small text-uppercase fw-bold mb-1" style={{ fontSize: '11px' }}>Asked Year</div>
              <div className="fw-bold text-warning" style={{ fontSize: '13px' }}>{questionData.year || questionData.askedYear || 'N/A'}</div>
            </div>
          </CCol>
          <CCol xs={6} sm={4} md={3} lg={2}>
            <div className="border p-2 rounded bg-light shadow-xs h-100">
              <div className="text-muted small text-uppercase fw-bold mb-1" style={{ fontSize: '11px' }}>Book Reference</div>
              <div className="fw-bold text-secondary text-truncate px-1" style={{ fontSize: '13px' }}>{questionData.bookReference || questionData.BookReference || 'N/A'}</div>
            </div>
          </CCol>
          <CCol xs={6} sm={4} md={3} lg={2}>
            <div className="border p-2 rounded bg-light shadow-xs h-100">
              <div className="text-muted small text-uppercase fw-bold mb-1" style={{ fontSize: '11px' }}>Complexity</div>
              <div className="fw-bold text-success text-truncate px-1" style={{ fontSize: '13px' }}>{questionData.complexity || questionData.Complexity || 'Easy'}</div>
            </div>
          </CCol>
        </CRow>
      </div>

      {/* Action Row */}
      <div className="bg-white border rounded p-3 shadow-sm mb-4">
        <CRow className="align-items-center g-3">
          <CCol xs={12} sm={6} className="d-flex align-items-center gap-2">
            <span className="fw-bold text-dark" style={{ fontSize: '0.95rem' }}>Social Media Icon to Share:</span>
            <div className="d-flex gap-2">
              <button onClick={() => handleShare('facebook')} className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center" style={{ borderRadius: '50%', width: '32px', height: '32px', padding: '0' }}><FaFacebookF size={14} /></button>
              <button onClick={() => handleShare('whatsapp')} className="btn btn-sm btn-outline-success d-flex align-items-center justify-content-center" style={{ borderRadius: '50%', width: '32px', height: '32px', padding: '0' }}><FaWhatsapp size={15} /></button>
              <button onClick={() => handleShare('twitter')} className="btn btn-sm btn-outline-dark d-flex align-items-center justify-content-center" style={{ borderRadius: '50%', width: '32px', height: '32px', padding: '0' }}><FaXTwitter size={14} /></button>
            </div>
          </CCol>
          <CCol xs={12} sm={6} className="d-flex align-items-center gap-2 justify-content-sm-end">
            <span className="fw-bold text-dark" style={{ fontSize: '0.95rem' }}>Download this as PDF:</span>
            <button 
              onClick={handleDirectDownloadPDF} 
              disabled={downloadingPdf}
              className="btn btn-sm btn-danger py-2 px-3 fw-bold d-flex align-items-center gap-2 shadow-sm" 
              style={{ borderRadius: '6px' }}
            >
              {downloadingPdf ? '⏳ Downloading...' : '📄 Download PDF'}
            </button>
          </CCol>
        </CRow>
      </div>

      {/* Core Question Content */}
      <CCard className="shadow-sm border-0 mb-4">
        <CCardBody className="p-4 bg-white rounded border">
          <div className="mb-4">
            <h5 className="fw-bold text-primary d-flex align-items-center gap-2 mb-3">❓ Question Statement:</h5>
            <div 
              className="p-3 bg-light rounded text-dark border-start border-primary border-4 shadow-xs" 
              style={{ 
                fontSize: '1.15rem', 
                lineHeight: '1.8', 
                wordBreak: 'break-word',
                overflowWrap: 'anywhere'
              }}
            >
              <MathRenderer htmlContent={questionStatementContent} />
            </div>
          </div>

          {!isSubjectiveMode && (
            <div className="mb-5">
              <h5 className="fw-bold text-secondary d-flex align-items-center gap-2 mb-3">🎯 Options Array Matrix:</h5>
              <CRow className="g-3">
                {activeOptions.map((opt) => {
                  const colWidth = opt.key === 'optionE' ? 12 : 6;
                  return (
                    <CCol xs={12} md={colWidth} key={opt.key}>
                      <div className="d-flex align-items-center gap-2 border p-3 rounded bg-white shadow-xs w-100 h-100 overflow-hidden" style={{ borderLeft: '4px solid #ced4da' }}>
                        <span className="fw-bold text-primary px-1" style={{ fontSize: '1.1rem' }}>({opt.label})</span>
                        <div className="flex-grow-1 text-dark m-0 p-0 d-inline-block align-middle overflow-hidden" style={{ width: 'calc(100% - 35px)' }}>
                          <MathRenderer htmlContent={opt.value} />
                        </div>
                      </div>
                    </CCol>
                  );
                })}
              </CRow>
            </div>
          )}

          <AnnouncementBanner position="between-q-sol" />

          {/* Solution Section */}
          <div className="mt-4 pt-3 border-top">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold text-success m-0 d-flex align-items-center gap-2">💡 Step-by-Step Solution / Proof:</h5>
              <CButton color={showSolution ? "secondary" : "success"} size="sm" className="fw-bold text-white px-3 py-1.5 shadow-sm" onClick={handleToggleSolution}>
                {showSolution ? '🙈 Hide Solution' : '👁️ View Solution'}
              </CButton>
            </div>
            <CCollapse visible={showSolution}>
              <div className="p-3 rounded border-start border-success border-4 shadow-xs overflow-hidden" style={{ backgroundColor: '#f4faf6', minHeight: '100px', fontSize: '1.15rem' }}>
                <MathRenderer htmlContent={solutionContent} />
              </div>
            </CCollapse>
          </div>
        </CCardBody>
      </CCard>
    </CContainer>
  );
};

export default ViewQuestion;